<template>
  <div class="app-container tracabilite-ascendante">

    <!-- ══════════════════════════════════════════════════════════════
         EN-TÊTE
    ══════════════════════════════════════════════════════════════ -->
    <div class="page-header">
      <div class="page-header__left">
        <h2><i class="el-icon-top" /> Traçabilité Ascendante</h2>
        <p class="page-header__subtitle">
          Reconstituer la chaîne : <strong>Producteur / Parcelle → Sacs → Collectes → Lots → Documents</strong>
        </p>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════
         PANNEAU DE RECHERCHE
    ══════════════════════════════════════════════════════════════ -->
    <el-card class="search-card" shadow="always">
      <div slot="header" class="card-header">
        <i class="el-icon-search" />
        <span>Critères de recherche</span>
      </div>

      <!-- Mode de recherche -->
      <div class="search-section">
        <p class="section-label">Type d'origine</p>
        <el-radio-group v-model="form.mode" size="medium" @change="resetResults">
          <el-radio-button label="producteur">
            <i class="el-icon-user" /> Producteur
          </el-radio-button>
          <el-radio-button label="parcelle">
            <i class="el-icon-map-location" /> Parcelle
          </el-radio-button>
        </el-radio-group>
      </div>

      <!-- Champs dynamiques selon le mode -->
      <div class="search-fields">

        <!-- Producteur : autocomplete dynamique -->
        <el-form v-if="form.mode === 'producteur'" :inline="true" class="search-form">
          <el-form-item label="Producteur">
            <el-select
              v-model="form.producteur_select"
              filterable
              remote
              clearable
              :remote-method="onProducteurInput"
              :loading="loadingProducteurs"
              placeholder="Tapez un nom, prénom ou code..."
              style="width: 360px;"
              no-data-text="Aucun producteur trouvé"
              @change="onProducteurSelect"
              @clear="onProducteurClear"
            >
              <el-option
                v-for="p in producteurs"
                :key="p.id"
                :label="p.nom + ' ' + p.prenom + (p.code ? ' · ' + p.code : '')"
                :value="p.id"
              >
                <div class="producteur-option">
                  <span class="producteur-option__name">{{ p.nom }} {{ p.prenom }}</span>
                  <el-tag v-if="p.code" size="mini" effect="plain" type="info" style="margin-left:8px;">{{ p.code }}</el-tag>
                  <span v-if="p.groupement" class="producteur-option__group">{{ p.groupement }}</span>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
          <!-- Indicateur producteur sélectionné -->
          <div v-if="form.producteur_select && !loadingProducteurs" class="selected-hint">
            <i class="el-icon-success" style="color:#67c23a;" />
            Producteur sélectionné : <strong>{{ getSelectedProducteurName() }}</strong>
          </div>
          <div v-else-if="loadingProducteurs" class="selected-hint">
            <i class="el-icon-loading" style="color:#409eff;" /> Chargement des producteurs...
          </div>
        </el-form>

        <!-- Parcelle : autocomplete dynamique -->
        <el-form v-if="form.mode === 'parcelle'" :inline="true" class="search-form">
          <el-form-item label="Parcelle">
            <el-select
              v-model="form.parcelle_select"
              filterable
              remote
              clearable
              :remote-method="onParcelleInput"
              :loading="loadingParcelles"
              placeholder="Tapez le code ou le libellé..."
              style="width: 360px;"
              no-data-text="Aucune parcelle trouvée"
              @change="onParcelleSelect"
              @clear="onParcelleClear"
            >
              <el-option
                v-for="p in parcelles"
                :key="p.id"
                :label="p.code + (p.libelle ? ' - ' + p.libelle : '')"
                :value="p.id"
              >
                <div class="parcelle-option">
                  <span class="parcelle-option__code">{{ p.code }}</span>
                  <span class="parcelle-option__libelle">{{ p.libelle }}</span>
                  <span v-if="p.superficie" class="parcelle-option__superficie">{{ p.superficie }} ha</span>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
          <!-- Indicateur parcelle sélectionnée -->
          <div v-if="form.parcelle_select && !loadingParcelles" class="selected-hint">
            <i class="el-icon-success" style="color:#67c23a;" />
            Parcelle sélectionnée : <strong>{{ getSelectedParcelleName() }}</strong>
          </div>
          <div v-else-if="loadingParcelles" class="selected-hint">
            <i class="el-icon-loading" style="color:#409eff;" /> Chargement des parcelles...
          </div>
        </el-form>
      </div>

      <!-- Filtres optionnels -->
      <el-collapse v-model="showFiltres" class="filtres-collapse">
        <el-collapse-item title="Filtres optionnels (affiner les résultats)" name="filtres">
          <el-form :inline="true" class="search-form">
            <el-form-item label="Campagne">
              <el-select v-model="form.campagne_id" clearable placeholder="Toutes les campagnes" style="width: 220px;">
                <el-option
                  v-for="c in campagnes"
                  :key="c.id"
                  :label="c.periode"
                  :value="c.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="Date de début">
              <el-date-picker
                v-model="form.date_debut"
                type="date"
                placeholder="Date de début"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                clearable
              />
            </el-form-item>
            <el-form-item label="Date de fin">
              <el-date-picker
                v-model="form.date_fin"
                type="date"
                placeholder="Date de fin"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                :picker-options="dateFinOptions"
                clearable
              />
            </el-form-item>
          </el-form>
        </el-collapse-item>
      </el-collapse>

      <!-- Bouton lancer -->
      <div class="search-actions">
        <el-button
          type="primary"
          icon="el-icon-search"
          size="medium"
          :loading="loading"
          @click="lancer"
        >
          {{ loading ? 'Recherche en cours...' : 'Lancer la recherche' }}
        </el-button>
        <el-button
          v-if="searched"
          icon="el-icon-refresh"
          size="medium"
          @click="reinitialiser"
        >
          Réinitialiser
        </el-button>
      </div>
    </el-card>

    <!-- ══════════════════════════════════════════════════════════════
         INDICATEUR DE CHARGEMENT
    ══════════════════════════════════════════════════════════════ -->
    <div v-if="loading" class="loading-block">
      <i class="el-icon-loading" />
      <span>Recherche en cours, veuillez patienter...</span>
    </div>

    <!-- ══════════════════════════════════════════════════════════════
         AUCUN RÉSULTAT
    ══════════════════════════════════════════════════════════════ -->
    <el-card v-if="!loading && searched && !hasResults" class="empty-card" shadow="never">
      <el-empty
        description="Aucune information disponible pour cette origine."
      >
        <el-button type="primary" @click="reinitialiser">Modifier les critères</el-button>
      </el-empty>
    </el-card>

    <!-- ══════════════════════════════════════════════════════════════
         RÉSULTATS
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="!loading && hasResults && result">

      <!-- Bandeau résumé -->
      <div class="results-summary">
        <el-tag type="info" effect="plain" size="medium">
          <i class="el-icon-data-line" />
          Origine : {{ result.origine.type === 'producteur' ? 'Producteur' : 'Parcelle' }}
        </el-tag>
        <el-tag type="success" effect="plain" size="medium">
          <i class="el-icon-box" /> {{ meta.total_sacs }} sacs
        </el-tag>
        <el-tag type="warning" effect="plain" size="medium">
          <i class="el-icon-date" /> {{ meta.total_collectes }} collectes
        </el-tag>
        <el-tag type="danger" effect="plain" size="medium">
          <i class="el-icon-truck" /> {{ meta.total_lots }} lots
        </el-tag>
        <el-tag effect="plain" size="medium">
          <i class="el-icon-odometer" /> Poids total : {{ meta.total_sacs_weight ? (meta.total_sacs_weight / 1000).toFixed(2) : '0' }} T
        </el-tag>
      </div>

      <!-- Informations de l'origine -->
      <el-card class="origine-card" shadow="never">
        <div class="row d-flex justify-content-between align-items-center">
          <div slot="header" class="card-header">
            <i :class="result.origine.type === 'producteur' ? 'el-icon-user' : 'el-icon-map-location'" />
            <span>Origine : {{ result.origine.type === 'producteur' ? 'Producteur' : 'Parcelle' }}</span>
          </div>
          <!-- Bouton carte si parcelle avec GPS -->
          <div style="display: flex;" class="gap-2">
            <div v-if="result.origine.type === 'parcelle' && result.origine.gps" class="origine-actions">
              <el-button type="primary" size="small" icon="el-icon-map-location" @click="voirCarteOrigine">
                Voir sur la carte
              </el-button>
            </div>
            <div v-if="result.origine.type === 'parcelle'" class="origine-actions">
              <el-button type="default" size="small" icon="el-icon-download" @click="telechargerFichierControleInterne">
                Fiche contrôle interne
              </el-button>
            </div>
          </div>
        </div>
        <el-descriptions v-if="result.origine.type === 'producteur'" :column="3" border size="small">
          <el-descriptions-item label="Code">{{ result.origine.code }}</el-descriptions-item>
          <el-descriptions-item label="Nom">{{ result.origine.nom }}</el-descriptions-item>
          <el-descriptions-item label="Prénom">{{ result.origine.prenom }}</el-descriptions-item>
          <el-descriptions-item v-if="result.origine.groupement" label="Groupement">
            {{ result.origine.groupement.nom }} ({{ result.origine.groupement.code }})
          </el-descriptions-item>
        </el-descriptions>

        <!-- Documents du producteur -->
        <div v-if="result.origine.type === 'producteur'" class="producteur-documents">
          <h4><i class="el-icon-document" /> Documents du producteur</h4>
          <div class="doc-actions-grid">
            <div class="doc-action">
              <span><i class="el-icon-document-checked" /> Fiche contrat</span>
              <div>
                <el-button
                  v-if="result.origine.fiche_contrat"
                  size="small"
                  type="primary"
                  plain
                  @click="voirDocument(result.origine.fiche_contrat)"
                >
                  <i class="el-icon-view" /> Voir
                </el-button>
                <el-button
                  v-if="result.origine.fiche_contrat"
                  size="small"
                  type="success"
                  plain
                  @click="telecharger(result.origine.fiche_contrat, 'fiche_contrat_' + result.origine.code + '.pdf')"
                >
                  <i class="el-icon-download" /> Télécharger
                </el-button>
                <el-tag v-else size="small" type="info">Non disponible</el-tag>
              </div>
            </div>
            <div class="doc-action">
              <span><i class="el-icon-document-checked" /> Fiche identification parcellaire</span>
              <div>
                <el-button
                  v-if="result.origine.fiche_identification_parcellaire"
                  size="small"
                  type="primary"
                  plain
                  @click="voirDocument(result.origine.fiche_identification_parcellaire)"
                >
                  <i class="el-icon-view" /> Voir
                </el-button>
                <el-button
                  v-if="result.origine.fiche_identification_parcellaire"
                  size="small"
                  type="success"
                  plain
                  @click="telecharger(result.origine.fiche_identification_parcellaire, 'fiche_identification_' + result.origine.code + '.pdf')"
                >
                  <i class="el-icon-download" /> Télécharger
                </el-button>
                <el-tag v-else size="small" type="info">Non disponible</el-tag>
              </div>
            </div>
          </div>
        </div>

        <el-descriptions v-if="result.origine.type === 'parcelle'" :column="3" border size="small">
          <el-descriptions-item label="Code">{{ result.origine.code }}</el-descriptions-item>
          <el-descriptions-item label="Libellé">{{ result.origine.libelle }}</el-descriptions-item>
          <el-descriptions-item label="Superficie">{{ result.origine.superficie }} ha</el-descriptions-item>
          <el-descriptions-item v-if="result.origine.village" label="Village">
            {{ result.origine.village.nom }}
          </el-descriptions-item>
          <el-descriptions-item v-if="result.origine.gps" label="GPS">
            {{ result.origine.gps.lat }}, {{ result.origine.gps.lng }}
          </el-descriptions-item>
        </el-descriptions>

        <!-- Producteur associé à la parcelle -->
        <div v-if="result.origine.type === 'parcelle' && result.origine.producteur" class="producteur-section">
          <h4><i class="el-icon-user" /> Producteur associé</h4>
          <el-descriptions :column="3" border size="small" style="margin-top: 12px;">
            <el-descriptions-item label="Code">{{ result.origine.producteur.code }}</el-descriptions-item>
            <el-descriptions-item label="Nom">{{ result.origine.producteur.nom }}</el-descriptions-item>
            <el-descriptions-item label="Prénom">{{ result.origine.producteur.prenom }}</el-descriptions-item>
          </el-descriptions>
          <!-- Documents du producteur -->
          <div class="producteur-actions" style="margin-top: 16px;">
            <div class="doc-actions-grid">
              <div class="doc-action">
                <span><i class="el-icon-document-checked" /> Fiche contrat</span>
                <div>
                  <el-button
                    v-if="result.origine.producteur.fiche_contrat"
                    size="mini"
                    type="primary"
                    plain
                    @click="voirDocument(result.origine.producteur?.fiche_contrat)"
                  >
                    <i class="el-icon-view" /> Voir
                  </el-button>
                  <el-button
                    v-if="result.origine.producteur.fiche_contrat"
                    size="mini"
                    type="success"
                    plain
                    @click="telechargerFicheContrat(result.origine.producteur)"
                  >
                    <i class="el-icon-download" /> Télécharger
                  </el-button>
                  <el-tag v-else size="mini" type="info">Non disponible</el-tag>
                </div>
              </div>
              <div class="doc-action">
                <span><i class="el-icon-document-checked" /> Fiche identification parcellaire</span>
                <div>
                  <el-button
                    v-if="result.origine.producteur.fiche_identification_parcellaire"
                    size="mini"
                    type="primary"
                    plain
                    @click="voirDocument(result.origine.producteur?.fiche_identification_parcellaire)"
                  >
                    <i class="el-icon-view" /> Voir
                  </el-button>
                  <el-button
                    v-if="result.origine.producteur.fiche_identification_parcellaire"
                    size="mini"
                    type="success"
                    plain
                    @click="telechargerFicheContrat(result.origine.producteur)"
                  >
                    <i class="el-icon-download" /> Télécharger
                  </el-button>
                  <el-tag v-else size="mini" type="info">Non disponible</el-tag>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- Arborescence des résultats -->
      <div class="results-tree">

        <!-- Collectes -->
        <el-collapse v-model="openSections" class="tree-collapse">
          <el-collapse-item name="collectes" class="tree-section">
            <template slot="title">
              <div class="section-title">
                <i class="el-icon-date" /> Collectes ({{ result.collectes.length }})
              </div>
            </template>
            <div class="section-content">
              <el-table :data="result.collectes" border stripe size="mini" style="width: 100%;">
                <el-table-column label="Code collecte" prop="code" min-width="120" />
                <el-table-column label="Date récolte" prop="date_recolt" min-width="120" />
                <el-table-column label="Parcelle" min-width="150">
                  <template slot-scope="{ row }">
                    {{ row.parcelle.code }} - {{ row.parcelle.libelle }}
                  </template>
                </el-table-column>
                <el-table-column label="Nb sacs" prop="sacs_count" min-width="80" />
                <el-table-column label="Poids (T)" prop="poids_total" min-width="100" />
                <el-table-column label="Actions" min-width="180">
                  <template slot-scope="{ row }">
                    <el-button
                      v-if="result.origine.type === 'producteur' && row.parcelle.gps"
                      size="mini"
                      type="primary"
                      plain
                      icon="el-icon-map-location"
                      @click="voirParcelleSurCarte(row.parcelle)"
                    >
                      Carte
                    </el-button>
                    <el-button
                      size="mini"
                      type="success"
                      plain
                      icon="el-icon-box"
                      @click="voirSacsCollecte(row)"
                    >
                      Voir sacs
                    </el-button>
                    <el-tag v-if="!row.parcelle.gps" size="mini" type="info">Pas de GPS</el-tag>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-collapse-item>

          <!-- Lots -->
          <el-collapse-item name="lots" class="tree-section">
            <template slot="title">
              <div class="section-title">
                <i class="el-icon-truck" /> Lots ({{ result.lots.length }})
              </div>
            </template>
            <div class="section-content">
              <el-table :data="result.lots" border stripe size="mini" style="width: 100%;">
                <el-table-column label="Code lot" prop="code" min-width="120" />
                <el-table-column label="Immatriculation" prop="im_camion" min-width="120" />
                <el-table-column label="Date chargement" prop="date_chrgmnt" min-width="120" />
                <el-table-column label="Campagne" min-width="120">
                  <template slot-scope="{ row }">
                    {{ row.campagne ? row.campagne.periode : '—' }}
                  </template>
                </el-table-column>
                <el-table-column label="Produit" min-width="120">
                  <template slot-scope="{ row }">
                    {{ row.produit ? row.produit.nom : '—' }}
                  </template>
                </el-table-column>
                <el-table-column label="Nb sacs" prop="sacs_count" min-width="80" />
                <el-table-column label="Poids (T)" prop="poids_total" min-width="100" />
                <el-table-column label="Fiche de collecte" min-width="150">
                  <template slot-scope="{ row }">
                    <div v-if="row.fiche_collecte" class="doc-actions-inline">
                      <el-button
                        size="mini"
                        type="primary"
                        plain
                        @click="voirDocument(row.fiche_collecte)"
                      >
                        <i class="el-icon-view" /> Voir
                      </el-button>
                      <el-button
                        size="mini"
                        type="success"
                        plain
                        @click="telecharger(row.fiche_collecte, 'fiche_collecte_' + row.code + '.pdf')"
                      >
                        <i class="el-icon-download" /> Télécharger
                      </el-button>
                    </div>
                    <el-tag v-else size="mini" type="info">Non disponible</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="Bon de livraison" min-width="150">
                  <template slot-scope="{ row }">
                    <div v-if="row.bon_livraison" class="doc-actions-inline">
                      <el-button
                        size="mini"
                        type="primary"
                        plain
                        @click="voirDocument(row.bon_livraison)"
                      >
                        <i class="el-icon-view" /> Voir
                      </el-button>
                      <el-button
                        size="mini"
                        type="success"
                        plain
                        @click="telecharger(row.bon_livraison, 'bon_livraison_' + row.code + '.pdf')"
                      >
                        <i class="el-icon-download" /> Télécharger
                      </el-button>
                    </div>
                    <el-tag v-else size="mini" type="info">Non disponible</el-tag>
                  </template>
                </el-table-column>

                <!-- Nouvelles colonnes Dolibarr -->
                <el-table-column label="Liens Dolibarr" min-width="220">
                  <template slot-scope="{ row }">
                    <div class="doc-actions-inline">
                      <!-- Boutons dynamiques SAU -->
                      <template v-if="lotsSauLinks[row.id] && lotsSauLinks[row.id].length > 0">
                        <el-button
                          v-for="(link, index) in lotsSauLinks[row.id]"
                          :key="'sau-'+row.id+'-'+index"
                          type="primary"
                          size="mini"
                          icon="el-icon-link"
                          @click="afficherDolibarr(link, 'sau', row.id)"
                        >
                          {{ formatLinkName(link.dol_object_type) }} {{ link.ref ? '(' + link.ref + ')' : '' }} (SAU)
                        </el-button>
                      </template>

                      <!-- Boutons dynamiques TRADING -->
                      <template v-if="lotsTradingLinks[row.id] && lotsTradingLinks[row.id].length > 0">
                        <el-button
                          v-for="(link, index) in lotsTradingLinks[row.id]"
                          :key="'trading-'+row.id+'-'+index"
                          type="success"
                          size="mini"
                          icon="el-icon-link"
                          @click="afficherDolibarr(link, 'trading', row.id)"
                        >
                          {{ formatLinkName(link.dol_object_type) }} {{ link.ref ? '(' + link.ref + ')' : '' }} (TRADING)
                        </el-button>
                      </template>

                      <!-- Indicateurs de chargement -->
                      <i v-if="loadingSauLinks[row.id] || loadingTradingLinks[row.id]" class="el-icon-loading" />

                      <span v-else-if="(!lotsSauLinks[row.id] || lotsSauLinks[row.id].length === 0) && (!lotsTradingLinks[row.id] || lotsTradingLinks[row.id].length === 0) && searched" style="color: #909399; font-size: 12px;">
                        Aucun lien
                      </span>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </template>

    <!-- Modal pour la carte -->
    <parcelle-map
      :visible.sync="mapVisible"
      :parcelles="mapParcelles"
    />

    <!-- Modal Fiche BL -->
    <el-dialog :title="'Informations Fiche BL (' + (fichebl.source || '') + ')'" :visible.sync="ficheBLDialog" width="50%" append-to-body>
      <div v-loading="ficheBLLoading">
        <div v-if="fichebl && fichebl.id">
          <table class="border centpercent" style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 35%;">Référence BL</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.ref }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Réf client</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.ref_client || 'N/A' }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Exportateur</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.exportateur }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Destinataire</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.destinataire }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">N° de BL</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.numero_bl || 'N/A' }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Nb de conteneurs</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.nb_conteneur }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Tonnage BL</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.tonnage_bl }} t</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Port de départ</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.port_depart }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Port de livraison</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.port_livraison }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">ETD Bateau</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.etd_bateau | formatDate }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">ETA Bateau</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.eta_bateau | formatDate }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Compagnie Maritime</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.compagnie_maritime }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Transitaire</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.transitaire }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Point de stockage</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.point_stockage }}</td>
            </tr>
          </table>
          <div style="margin-top: 20px; text-align: center;">
            <el-link :href="fichebl.web_url" target="_blank" type="primary" icon="el-icon-link">Ouvrir dans {{ fichebl.source }} (Dolibarr)</el-link>
          </div>
        </div>
        <div v-else-if="!ficheBLLoading" style="text-align: center; padding: 20px;">
          <el-alert title="Aucun Bon de Livraison lié trouvé pour ce lot." type="info" show-icon />
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="ficheBLDialog = false">Fermer</el-button>
      </span>
    </el-dialog>

    <!-- Modal Commande -->
    <el-dialog :title="'Informations Commande (' + (commande.source || '') + ')'" :visible.sync="commandeDialog" width="50%" append-to-body>
      <div v-loading="commandeLoading">
        <div v-if="commande && commande.id">
          <table class="border centpercent" style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 35%;">Référence Interne</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.ref }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Réf. Fournisseur</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.ref_supplier || 'N/A' }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Fournisseur</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.fournisseur }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Statut</td>
              <td style="padding: 8px; border: 1px solid #ddd;">
                <el-tag size="small">{{ commande.statut_label }}</el-tag>
              </td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Date commande</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.date_commande | formatDate }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Date livraison prévue</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.date_livraison | formatDate }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Total HT</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.total_ht }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Total TVA</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.total_tva }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Total TTC</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.total_ttc }}</td>
            </tr>
          </table>
          <div style="margin-top: 20px; text-align: center;">
            <el-link :href="commande.web_url" target="_blank" type="primary" icon="el-icon-link">Ouvrir dans {{ commande.source }} (Dolibarr)</el-link>
          </div>
        </div>
        <div v-else-if="!commandeLoading" style="text-align: center; padding: 20px;">
          <el-alert title="Aucune Commande liée trouvée pour ce lot." type="info" show-icon />
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="commandeDialog = false">Fermer</el-button>
      </span>
    </el-dialog>

    <!-- ══════════════════════════════════════════════════════════════
         DRAWER – SACS D'UNE COLLECTE
    ══════════════════════════════════════════════════════════════ -->
    <el-drawer
      :title="'Sacs – ' + (drawerCollecte ? drawerCollecte.code : '')"
      :visible.sync="drawer.sacs"
      direction="rtl"
      size="70%"
    >
      <div v-loading="drawer.loading" class="drawer-content">
        <div v-if="drawerData.sacs" class="drawer-summary">
          <el-tag type="info">{{ drawerData.sacs.length }} sacs</el-tag>
          <el-tag type="success">{{ (drawerData.sacs.reduce((sum, sac) => sum + sac.poids, 0) / 1000).toFixed(2) }} T total</el-tag>
          <el-button size="mini" icon="el-icon-download" style="float:right" @click="exporterSacs(drawerData.sacs)">
            Exporter la liste
          </el-button>
        </div>
        <el-table
          v-if="drawerData.sacs"
          :data="drawerData.sacs"
          border
          stripe
          size="mini"
          style="width: 100%; margin-top: 12px;"
        >
          <el-table-column label="Code sac" prop="code" min-width="120" />
          <el-table-column label="Code lot" min-width="120">
            <template slot-scope="{ row }">
              {{ row.lot ? row.lot.code : '---' }}
            </template>
          </el-table-column>
          <el-table-column label="Poids (Kg)" prop="poids" min-width="100" />
          <el-table-column label="Date récolte" min-width="120">
            <template slot-scope="{ row }">
              {{ row.recolte ? row.recolte.date_recolt : '---' }}
            </template>
          </el-table-column>
          <el-table-column label="Code récolte" min-width="120">
            <template slot-scope="{ row }">
              {{ row.recolte ? row.recolte.code : '---' }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>

  </div>
</template>

<script>
import tracabiliteApi from '@/api/tracabilite';
import Resource from '@/api/resource';
import ParcelleMap from '@/components/ParcelleMap.vue';

const campagneResource = new Resource('campagne_agricoles');
const producteurResource = new Resource('producteurs-all');
const parcelleResource = new Resource('parcelles');

export default {
  name: 'TracabiliteAscendante',
  components: {
    ParcelleMap,
  },

  filters: {
    formatDate(value) {
      if (!value) {
        return '';
      }
      if (typeof value !== 'string') {
        const d = new Date(value);
        if (isNaN(d.getTime())) {
          return value;
        }
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      }
      const parts = value.split('-');
      if (parts.length < 3) {
        return value;
      }
      const [y, m, d] = parts;
      return `${d}/${m}/${y}`;
    },
  },

  data() {
    return {
      // ── Formulaire de recherche ─────────────────────────────────
      form: {
        mode: 'producteur',
        producteur_select: null,
        parcelle_select: null,
        campagne_id: null,
        date_debut: null,
        date_fin: null,
      },

      // ── États UI ────────────────────────────────────────────────
      loading: false,
      searched: false,
      showFiltres: [],

      // ── États des drawers ─────────────────────────────────────────
      drawer: {
        sacs: false,
      },
      drawerCollecte: null,
      drawerLoading: false,

      // ── Données résultats ───────────────────────────────────────
      result: null,
      meta: {
        total_sacs: 0,
        total_collectes: 0,
        total_lots: 0,
        total_sacs_weight: 0,
        displayed_sacs: 0,
        displayed_collectes: 0,
        displayed_lots: 0,
      },
      openSections: ['collectes', 'lots'],

      // ── Données des drawers ─────────────────────────────────────
      drawerData: {
        sacs: null,
      },

      // ── Données des selects ─────────────────────────────────────
      campagnes: [],
      producteurs: [],
      parcelles: [],
      loadingProducteurs: false,
      loadingParcelles: false,
      debounceProducteur: null,
      debounceParcelle: null,

      // ── Carte ───────────────────────────────────────────────────
      mapVisible: false,
      mapParcelles: [],

      // Dolibarr Integration
      ficheBLDialog: false,
      fichebl: {},
      ficheBLLoading: false,
      commandeDialog: false,
      commande: {},
      commandeLoading: false,
      lotsSauLinks: {},
      lotsTradingLinks: {},
      loadingSauLinks: {},
      loadingTradingLinks: {},
      loadingLinks: {},
      dolibarrApiKey: '',
      sauUrl: '',
      tradingUrl: '',

      // Options date picker
      dateFinOptions: {
        disabledDate: (date) => {
          if (!this.form.date_debut) {
            return false;
          }
          return date < new Date(this.form.date_debut);
        },
      },
    };
  },

  computed: {
    hasResults() {
      return this.result && (
        (this.result.sacs && this.result.sacs.length > 0) ||
        (this.result.collectes && this.result.collectes.length > 0) ||
        (this.result.lots && this.result.lots.length > 0)
      );
    },
  },

  created() {
    this.loadCampagnes();
  },

  methods: {
    // ── Chargement des données référentielles ─────────────────────
    async loadCampagnes() {
      try {
        const { data } = await campagneResource.list({ limit: 50 });
        this.campagnes = data || [];
      } catch (e) {
        console.error('Erreur chargement campagnes', e);
      }
    },

    // ── Autocomplete producteur avec debounce 300ms ───────────────
    onProducteurInput(query) {
      clearTimeout(this.debounceProducteur);
      if (!query || query.length < 1) {
        this.producteurs = [];
        return;
      }
      this.debounceProducteur = setTimeout(() => {
        this.searchProducteurs(query);
      }, 300);
    },

    async searchProducteurs(query) {
      this.loadingProducteurs = true;
      try {
        const { data } = await producteurResource.list({ keyword: query, limit: 30 });
        this.producteurs = (data || []).map(p => ({
          id: p.id,
          nom: p.nom || '',
          prenom: p.prenom || '',
          code: p.code || null,
          groupement: p.groupement ? p.groupement.nom : null,
        }));
      } catch (e) {
        console.error('Erreur recherche producteurs', e);
      } finally {
        this.loadingProducteurs = false;
      }
    },

    onProducteurSelect(producteurId) {
      // Le producteur est sélectionné via v-model
    },

    onProducteurClear() {
      this.form.producteur_select = null;
      this.producteurs = [];
    },

    getSelectedProducteurName() {
      const producteur = this.producteurs.find(p => p.id === this.form.producteur_select);
      return producteur ? `${producteur.nom} ${producteur.prenom}` : '';
    },

    // ── Autocomplete parcelle avec debounce 300ms ─────────────────
    onParcelleInput(query) {
      clearTimeout(this.debounceParcelle);
      if (!query || query.length < 1) {
        this.parcelles = [];
        return;
      }
      this.debounceParcelle = setTimeout(() => {
        this.searchParcelles(query);
      }, 300);
    },

    async searchParcelles(query) {
      this.loadingParcelles = true;
      try {
        const { data } = await parcelleResource.list({ keyword: query, limit: 30 });
        this.parcelles = data || [];
      } catch (e) {
        console.error('Erreur recherche parcelles', e);
      } finally {
        this.loadingParcelles = false;
      }
    },

    onParcelleSelect(parcelleId) {
      // La parcelle est sélectionnée via v-model
    },

    onParcelleClear() {
      this.form.parcelle_select = null;
      this.parcelles = [];
    },

    getSelectedParcelleName() {
      const parcelle = this.parcelles.find(p => p.id === this.form.parcelle_select);
      return parcelle ? `${parcelle.code}` : '';
    },

    // ── Lancement de la recherche ─────────────────────────────────
    async lancer() {
      // Validation côté client
      if (this.form.mode === 'producteur' && !this.form.producteur_select) {
        this.$message({ type: 'warning', message: 'Veuillez sélectionner un producteur.' });
        return;
      }
      if (this.form.mode === 'parcelle' && !this.form.parcelle_select) {
        this.$message({ type: 'warning', message: 'Veuillez sélectionner une parcelle.' });
        return;
      }

      this.loading = true;
      this.searched = false;
      this.result = null;
      this.lotsSauLinks = {};
      this.lotsTradingLinks = {};
      this.loadingSauLinks = {};
      this.loadingTradingLinks = {};
      this.loadingLinks = {};

      try {
        const params = {
          campagne_id: this.form.campagne_id || undefined,
          date_debut: this.form.date_debut || undefined,
          date_fin: this.form.date_fin || undefined,
        };

        if (this.form.mode === 'producteur') {
          params.producteur_id = this.form.producteur_select;
        } else {
          params.parcelle_id = this.form.parcelle_select;
        }

        const response = await tracabiliteApi.searchAscendante(params);

        this.result = response.data;

        // Calculer le poids total des sacs
        const totalWeight = response.data.sacs ? response.data.sacs.reduce((sum, sac) => sum + (sac.poids || 0), 0) : 0;

        this.meta = {
          ...response.meta,
          total_sacs_weight: totalWeight,
        };
        this.searched = true;

        // Récupérer les liens Dolibarr pour tous les lots trouvés
        if (this.result.lots && this.result.lots.length > 0) {
          this.getDolibarrLinksForLots(this.result.lots);
        }
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erreur lors de la recherche.';
        this.$message({ type: 'error', message: msg });
      } finally {
        this.loading = false;
      }
    },

    // ── Dolibarr Integration ─────────────────────────────────────────
    formatLinkName(type) {
      if (!type) {
        return 'Lien';
      }
      if (type === 'fichebl') {
        return 'Fiche BL';
      }
      return type.charAt(0).toUpperCase() + type.slice(1);
    },

    async getDolibarrLinksForLots(lots) {
      try {
        // Charger d'abord les credentials si on ne les a pas
        if (!this.dolibarrApiKey) {
          const response = await window.axios.get('/api/settings/api-key');
          this.dolibarrApiKey = response.data.data.api_key;
          this.sauUrl = response.data.data.dolibarr_url;
          this.tradingUrl = response.data.data.dolibarr_url_trading;
        }

        if (!this.dolibarrApiKey) {
          console.warn('Dolibarr integration not configured.');
          return;
        }

        // Récupérer les liens Dolibarr pour tous les lots trouvés
        lots.forEach((lot) => {
          this.checkLinksForLot(lot, 'sau');
          if (this.tradingUrl) {
            this.checkLinksForLot(lot, 'trading');
          }
        });
      } catch (error) {
        console.error('Erreur initialisation Dolibarr:', error);
      }
    },

    async checkLinksForLot(lot, source) {
      const url = source === 'sau' ? this.sauUrl : this.tradingUrl;
      const apiUrl = `${url}/custom/aaslink/api/get_lot_links.php`;
      const loadingState = source === 'sau' ? this.loadingSauLinks : this.loadingTradingLinks;

      try {
        this.$set(loadingState, lot.id, true);
        const response = await window.axios.get(apiUrl, {
          params: { lot_id: lot.id },
          headers: { 'X-API-KEY': this.dolibarrApiKey },
        });
        if (response.data.status === 'success' && response.data.data && response.data.data.length > 0) {
          if (source === 'sau') {
            this.$set(this.lotsSauLinks, lot.id, response.data.data);
          } else {
            this.$set(this.lotsTradingLinks, lot.id, response.data.data);
          }
        }
      } catch (e) {
        console.warn(`Erreur links Dolibarr ${source} pour lot ${lot.id}:`, e);
      } finally {
        this.$set(loadingState, lot.id, false);
      }
    },

    async afficherDolibarr(link, source, lotId) {
      const url = source === 'sau' ? this.sauUrl : this.tradingUrl;

      if (link.dol_object_type === 'fichebl') {
        this.ficheBLDialog = true;
        this.ficheBLLoading = true;
        this.fichebl = {};
        const apiUrl = `${url}/custom/aaslink/api/get_fichebl_by_lot.php`;
        try {
          const response = await window.axios.get(apiUrl, {
            params: { lot_id: lotId },
            headers: { 'X-API-KEY': this.dolibarrApiKey },
          });
          if (response.data.status === 'success') {
            this.fichebl = response.data.data;
            this.fichebl.source = source.toUpperCase();
          }
        } catch (e) {
          this.$message.error('Erreur chargement Fiche BL');
        } finally {
          this.ficheBLLoading = false;
        }
      } else if (link.dol_object_type === 'commande') {
        this.commandeDialog = true;
        this.commandeLoading = true;
        this.commande = {};
        const apiUrl = `${url}/custom/aaslink/api/get_commande_by_lot.php`;
        try {
          const response = await window.axios.get(apiUrl, {
            params: { lot_id: lotId },
            headers: { 'X-API-KEY': this.dolibarrApiKey },
          });
          if (response.data.status === 'success') {
            this.commande = response.data.data;
            this.commande.source = source.toUpperCase();
          }
        } catch (e) {
          this.$message.error('Erreur chargement Commande');
        } finally {
          this.commandeLoading = false;
        }
      }
    },

    resetResults() {
      this.result = null;
      this.searched = false;
      this.form.producteur_select = null;
      this.form.parcelle_select = null;
      this.producteurs = [];
      this.parcelles = [];
    },

    reinitialiser() {
      this.resetResults();
      this.form.campagne_id = null;
      this.form.date_debut = null;
      this.form.date_fin = null;
    },

    // ── Actions documents ─────────────────────────────────────────────
    voirDocument(url) {
      if (url) {
        window.open(url, '_blank');
      }
    },

    telecharger(url, filename) {
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    async telechargerFicheContrat(producteur) {
      const contratUrl = producteur.fiche_contrat;
      if (!contratUrl) {
        this.$message({ type: 'warning', message: 'Aucune fiche contrat disponible pour ce producteur.' });
        return;
      }
      // Extraire le nom du fichier depuis l'URL
      const filename = contratUrl.split('/').pop() || 'fiche_contrat.pdf';
      try {
        const response = await fetch(contratUrl, {
          headers: {
            'Accept': 'application/pdf',
            'Cache-Control': 'no-cache',
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message({ type: 'success', message: 'Fiche contrat téléchargée avec succès.' });
      } catch (error) {
        console.error('Erreur téléchargement fiche contrat:', error);
        this.$message({ type: 'error', message: `Erreur lors du téléchargement: ${error.message || 'Erreur inconnue'}` });
      }
    },

    // ── Carte ───────────────────────────────────────────────────────
    voirCarteOrigine() {
      if (this.result && this.result.origine && this.result.origine.gps) {
        this.mapParcelles = [{
          id: this.result.origine.id,
          code: this.result.origine.code,
          libelle: this.result.origine.libelle,
          gps: this.result.origine.gps,
        }];
        this.mapVisible = true;
      }
    },

    voirParcelleSurCarte(parcelle) {
      if (parcelle && parcelle.gps) {
        this.mapParcelles = [{
          id: parcelle.id,
          code: parcelle.code,
          libelle: parcelle.libelle,
          gps: parcelle.gps,
        }];
        this.mapVisible = true;
      }
    },

    async telechargerFicheIdentification(producteur) {
      const url = producteur.fiche_identification_parcellaire;
      if (!url) {
        this.$message({ type: 'warning', message: 'Aucune fiche d\'identification disponible.' });
        return;
      }
      this.telecharger(url, `fiche_identification_${producteur.code}.pdf`);
    },

    async telechargerFichierControleInterne() {
      if (!this.result || !this.result.origine || !this.result.origine.controle_interne_url) {
        this.$message({ type: 'warning', message: 'Aucun fichier de contrôle interne disponible pour cette parcelle.' });
        return;
      }

      this.telecharger(this.result.origine.controle_interne_url, `controle_interne_${this.result.origine.code}.pdf`);
    },

    // ── Drawer sacs ─────────────────────────────────────────────────
    voirSacsCollecte(collecte) {
      this.drawerCollecte = collecte;
      this.drawerData.sacs = null;
      this.drawerLoading = true;
      this.drawer.sacs = true;

      // Filtrer les sacs qui appartiennent à cette collecte
      const sacsDeLaCollecte = this.result.sacs.filter(sac => sac.recolte_id === collecte.id);

      this.drawerData.sacs = sacsDeLaCollecte.map(sac => ({
        ...sac,
        recolte: collecte, // Ajouter les infos de la collecte pour l'affichage
      }));

      this.drawerLoading = false;
    },

    exporterSacs(sacs) {
      // Exporter les sacs en CSV
      const headers = ['Code sac', 'Poids (Kg)', 'Date récolte', 'Code récolte'];
      const rows = sacs.map(sac => [
        sac.code,
        sac.poids,
        sac.recolte ? sac.recolte.date_recolt : '',
        sac.recolte ? sac.recolte.code : '',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `sacs_${this.drawerCollecte.code}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.tracabilite-ascendante {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
}

.page-header__left h2 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 24px;
}

.page-header__subtitle {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

.search-card {
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  font-weight: 600;
}

.card-header i {
  margin-right: 8px;
  color: #409eff;
}

/* Styles pour aligner le header */
.row.d-flex.justify-content-between.align-items-center {
  align-items: center;
  min-height: 40px; /* Assurer une hauteur minimale pour l'alignement */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.origine-actions {
  display: flex;
  align-items: center;
  height: 100%;
}

.search-section {
  margin-bottom: 20px;
}

.section-label {
  margin: 0 0 12px 0;
  font-weight: 600;
  color: #303133;
}

.search-fields {
  margin-bottom: 20px;
}

.search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.producteur-option,
.parcelle-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.producteur-option__name {
  font-weight: 600;
}

.producteur-option__group {
  color: #909399;
  font-size: 12px;
}

.parcelle-option__code {
  font-weight: 600;
  margin-right: 8px;
}

.parcelle-option__libelle {
  flex: 1;
}

.parcelle-option__superficie {
  color: #909399;
  font-size: 12px;
}

.selected-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9ff;
  border: 1px solid #e1f5fe;
  border-radius: 4px;
  font-size: 13px;
  color: #0277bd;
}

.filtres-collapse {
  margin-bottom: 20px;
}

.search-actions {
  display: flex;
  gap: 12px;
}

.loading-block {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: #f5f7fa;
  border-radius: 8px;
  margin-bottom: 24px;
}

.loading-block i {
  font-size: 24px;
  margin-right: 12px;
  color: #409eff;
}

.empty-card {
  margin-bottom: 24px;
}

.results-summary {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.origine-card {
  margin-bottom: 24px;
}

.origine-actions {
  margin-top: 16px;
  text-align: right;
}

.results-tree {
  margin-bottom: 24px;
}

.tree-collapse {
  border: none;
}

.tree-section {
  margin-bottom: 16px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #303133;
}

.section-title i {
  margin-right: 8px;
  color: #409eff;
}

.section-content {
  padding: 16px;
}

.document-item {
  margin-bottom: 24px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
}

.document-item:last-child {
  margin-bottom: 0;
}

.document-header {
  padding: 12px 16px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}

.document-header h4 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.document-actions {
  padding: 16px;
}

.doc-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
}

.doc-action:last-child {
  margin-bottom: 0;
}

.doc-action span {
  font-weight: 500;
  color: #606266;
}

.doc-action > div {
  display: flex;
  gap: 8px;
}

.doc-actions-inline {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* Styles pour les drawers */
.drawer-content {
  padding: 20px;
}

.drawer-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

/* Styles pour la section producteur */
.producteur-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e4e7ed;
}

.producteur-section h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.producteur-section h4 i {
  margin-right: 8px;
  color: #409eff;
}

.producteur-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.producteur-documents {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e4e7ed;
}

.producteur-documents h4 {
  margin: 0 0 16px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.producteur-documents h4 i {
  margin-right: 8px;
  color: #409eff;
}

.doc-actions-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
